/**
 * Luxury Finishes
 *
 * Stack of interactive before/after image sliders — one row per finish
 * area — comparing Phase 1 vs Phase 2, modeled on skyonmain.com/luxuryfinishes.
 *
 * Each row shows two full-size images stacked. The "after" (Phase 2) image
 * sits on top and is clipped from the right so the user sees Phase 1 on the
 * left and Phase 2 on the right of a draggable vertical divider. Captions
 * below each image name the specific finish area and phase.
 */

import { useCallback, useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  useGallery,
  usePropertyField,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';

// ============================================================================
// Slot group definitions — one per before/after comparison row. Repeated
// literals (not a factory) because the schema linter extracts them
// statically, one level deep. See component-anatomy.md.
// ============================================================================

const kitchenCompareDef: SlotGroupDefinition = {
  label: 'Kitchen Comparison',
  fields: {
    before_image: {
      label: 'Phase 1 Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    before_alt: {
      label: 'Phase 1 Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Kitchen — Phase 1',
    },
    before_label: {
      label: 'Phase 1 Caption',
      type: 'text',
      defaultValue: 'Kitchen Phase 1',
    },
    after_image: {
      label: 'Phase 2 Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
    },
    after_alt: {
      label: 'Phase 2 Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
      defaultValue: 'Kitchen — Phase 2',
    },
    after_label: {
      label: 'Phase 2 Caption',
      type: 'text',
      defaultValue: 'Kitchen Phase 2',
    },
  },
};

const bathroomCompareDef: SlotGroupDefinition = {
  label: 'A1 Bathroom Comparison',
  fields: {
    before_image: {
      label: 'Phase 1 Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
    },
    before_alt: {
      label: 'Phase 1 Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'A1 Bathroom — Phase 1',
    },
    before_label: {
      label: 'Phase 1 Caption',
      type: 'text',
      defaultValue: 'A1 Bathroom Phase 1',
    },
    after_image: {
      label: 'Phase 2 Image',
      type: 'image',
      defaultField: 'photo_gallery[3].image',
    },
    after_alt: {
      label: 'Phase 2 Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[3].alt_text',
      defaultValue: 'A1 Bathroom — Phase 2',
    },
    after_label: {
      label: 'Phase 2 Caption',
      type: 'text',
      defaultValue: 'A1 Bathroom Phase 2',
    },
  },
};

const livingCompareDef: SlotGroupDefinition = {
  label: 'A1 Living Comparison',
  fields: {
    before_image: {
      label: 'Phase 1 Image',
      type: 'image',
      defaultField: 'photo_gallery[4].image',
    },
    before_alt: {
      label: 'Phase 1 Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[4].alt_text',
      defaultValue: 'A1 open-concept living — Phase 1',
    },
    before_label: {
      label: 'Phase 1 Caption',
      type: 'text',
      defaultValue: 'A1 Phase 1',
    },
    after_image: {
      label: 'Phase 2 Image',
      type: 'image',
      defaultField: 'photo_gallery[5].image',
    },
    after_alt: {
      label: 'Phase 2 Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[5].alt_text',
      defaultValue: 'A1 open-concept living — Phase 2',
    },
    after_label: {
      label: 'Phase 2 Caption',
      type: 'text',
      defaultValue: 'A1 Phase 2',
    },
  },
};

const kitchenDetailCompareDef: SlotGroupDefinition = {
  label: 'Kitchen Finishes Comparison',
  fields: {
    before_image: {
      label: 'Phase 1 Image',
      type: 'image',
      defaultField: 'photo_gallery[6].image',
    },
    before_alt: {
      label: 'Phase 1 Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[6].alt_text',
      defaultValue: 'Kitchen finish detail — Phase 1',
    },
    before_label: {
      label: 'Phase 1 Caption',
      type: 'text',
      defaultValue: 'Kitchen Finishes Phase 1',
    },
    after_image: {
      label: 'Phase 2 Image',
      type: 'image',
      defaultField: 'photo_gallery[7].image',
    },
    after_alt: {
      label: 'Phase 2 Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[7].alt_text',
      defaultValue: 'Kitchen finish detail — Phase 2',
    },
    after_label: {
      label: 'Phase 2 Caption',
      type: 'text',
      defaultValue: 'Kitchen Finishes Phase 2',
    },
  },
};

const showerCompareDef: SlotGroupDefinition = {
  label: 'Shower Comparison',
  fields: {
    before_image: {
      label: 'Phase 1 Image',
      type: 'image',
      defaultField: 'photo_gallery[8].image',
    },
    before_alt: {
      label: 'Phase 1 Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[8].alt_text',
      defaultValue: 'Shower finishes — Phase 1',
    },
    before_label: {
      label: 'Phase 1 Caption',
      type: 'text',
      defaultValue: 'Shower Finishes Phase 1',
    },
    after_image: {
      label: 'Phase 2 Image',
      type: 'image',
      defaultField: 'photo_gallery[9].image',
    },
    after_alt: {
      label: 'Phase 2 Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[9].alt_text',
      defaultValue: 'Shower finishes — Phase 2',
    },
    after_label: {
      label: 'Phase 2 Caption',
      type: 'text',
      defaultValue: 'Shower Finishes Phase 2',
    },
  },
};

const laundryCompareDef: SlotGroupDefinition = {
  label: 'Washer and Dryer Comparison',
  fields: {
    before_image: {
      label: 'Phase 1 Image',
      type: 'image',
      defaultField: 'photo_gallery[10].image',
    },
    before_alt: {
      label: 'Phase 1 Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[10].alt_text',
      defaultValue: 'In-home washer and dryer — Phase 1',
    },
    before_label: {
      label: 'Phase 1 Caption',
      type: 'text',
      defaultValue: 'Washer and Dryer Phase 1',
    },
    after_image: {
      label: 'Phase 2 Image',
      type: 'image',
      defaultField: 'photo_gallery[11].image',
    },
    after_alt: {
      label: 'Phase 2 Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[11].alt_text',
      defaultValue: 'In-home washer and dryer — Phase 2',
    },
    after_label: {
      label: 'Phase 2 Caption',
      type: 'text',
      defaultValue: 'Washer and Dryer Phase 2',
    },
  },
};

const lockCompareDef: SlotGroupDefinition = {
  label: 'Lock Comparison',
  fields: {
    before_image: {
      label: 'Phase 1 Image',
      type: 'image',
      defaultField: 'photo_gallery[12].image',
    },
    before_alt: {
      label: 'Phase 1 Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[12].alt_text',
      defaultValue: 'Door lock — Phase 1',
    },
    before_label: {
      label: 'Phase 1 Caption',
      type: 'text',
      defaultValue: 'Lock Phase 1',
    },
    after_image: {
      label: 'Phase 2 Image',
      type: 'image',
      defaultField: 'photo_gallery[13].image',
    },
    after_alt: {
      label: 'Phase 2 Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[13].alt_text',
      defaultValue: 'Smart lock — Phase 2',
    },
    after_label: {
      label: 'Phase 2 Caption',
      type: 'text',
      defaultValue: 'Smart Lock Phase 2',
    },
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Select Your Floorplan' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Before/after slider — interactive drag UI
// ============================================================================

interface CompareData {
  before_image?: string | null;
  before_alt?: string | null;
  before_label?: string | null;
  after_image?: string | null;
  after_alt?: string | null;
  after_label?: string | null;
}

function BeforeAfterSlider({
  data,
  beforeFallbackSrc = '',
  afterFallbackSrc = '',
  beforeFallbackAlt = '',
  afterFallbackAlt = '',
}: {
  data: CompareData;
  /** Resolved image URL to use when `data.before_image` is empty (gallery short of the schema default index). */
  beforeFallbackSrc?: string;
  afterFallbackSrc?: string;
  beforeFallbackAlt?: string;
  afterFallbackAlt?: string;
}) {
  // One state variable; one native <input type="range"> drives everything.
  // No pointer events, no refs, no useEffect window listeners, no clip-path.
  // Click / drag / tap / keyboard-arrow all come free from the input.
  const [pos, setPos] = useState(50);

  const beforeSrc = data.before_image || beforeFallbackSrc || '';
  const afterSrc = data.after_image || afterFallbackSrc || beforeFallbackSrc || '';
  const beforeAlt =
    data.before_alt || beforeFallbackAlt || data.before_label || 'Before';
  const afterAlt =
    data.after_alt || afterFallbackAlt || data.after_label || 'After';
  const beforeLabel = data.before_label || 'Phase 1';
  const afterLabel = data.after_label || 'Phase 2';

  const posInt = Math.round(pos);
  const posPercent = `${pos}%`;
  // clip-path hides the right portion of the overlay image beyond the slider
  // position. The IMAGE itself never changes size or position — only the clip
  // boundary moves. Nothing scales or shifts as the user drags.
  const overlayClipPath = `inset(0 ${100 - pos}% 0 0)`;

  return (
    <figure className="lux-ba">
      <div className="lux-ba-stage">
        {/* Base layer: AFTER image fills the entire stage. */}
        {afterSrc ? (
          <VibesitesImage
            url={afterSrc}
            alt={afterAlt}
            sizes="(min-width: 1024px) 480px, (min-width: 640px) 50vw, 100vw"
            className="lux-ba-img lux-ba-img--base"
          />
        ) : (
          <div className="lux-ba-img lux-ba-img--base lux-ba-placeholder" aria-hidden="true" />
        )}

        {/* Overlay layer: BEFORE image, also sized to the entire stage, with
            clip-path trimming the right portion. The image never scales or
            moves while the user drags — only the clip edge moves. */}
        <div className="lux-ba-clip" style={{ clipPath: overlayClipPath }}>
          {beforeSrc ? (
            <VibesitesImage
              url={beforeSrc}
              alt={beforeAlt}
              sizes="(min-width: 1024px) 480px, (min-width: 640px) 50vw, 100vw"
              className="lux-ba-img lux-ba-img--overlay"
            />
          ) : (
            <div className="lux-ba-img lux-ba-img--overlay lux-ba-placeholder" aria-hidden="true" />
          )}
        </div>

        {/* Native range input — handles mouse, touch, keyboard, screen readers
            for free. Visually hidden; the knob + divider line below show state. */}
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={posInt}
          onChange={(e) => setPos(Number(e.target.value))}
          onInput={(e) => setPos(Number((e.target as HTMLInputElement).value))}
          className="lux-ba-range"
          aria-label={`Reveal more of ${beforeLabel} or ${afterLabel}`}
        />

        {/* Visual divider + knob. Pointer-events: none so the range input
            underneath keeps receiving every drag. */}
        <div
          className="lux-ba-divider"
          aria-hidden="true"
          style={{ left: posPercent }}
        >
          <div className="lux-ba-knob">
            <span className="lux-ba-arrow lux-ba-arrow--left" />
            <span className="lux-ba-arrow lux-ba-arrow--right" />
          </div>
        </div>
      </div>

      <figcaption className="lux-ba-captions">
        <span className="lux-ba-caption">{beforeLabel}</span>
        <span className="lux-ba-caption">{afterLabel}</span>
      </figcaption>
    </figure>
  );
}

// ============================================================================
// Component
// ============================================================================

export function LuxuryFinishes() {
  const kitchen = useSlotGroup('luxuryfinishes.compare_kitchen', kitchenCompareDef) as CompareData;
  const bathroom = useSlotGroup('luxuryfinishes.compare_bathroom', bathroomCompareDef) as CompareData;
  const living = useSlotGroup('luxuryfinishes.compare_living', livingCompareDef) as CompareData;
  const kitchenDetail = useSlotGroup(
    'luxuryfinishes.compare_kitchen_detail',
    kitchenDetailCompareDef,
  ) as CompareData;
  const shower = useSlotGroup('luxuryfinishes.compare_shower', showerCompareDef) as CompareData;
  const laundry = useSlotGroup('luxuryfinishes.compare_laundry', laundryCompareDef) as CompareData;
  const lock = useSlotGroup('luxuryfinishes.compare_lock', lockCompareDef) as CompareData;
  const cta = useSlotGroup('luxuryfinishes.cta', ctaDef) as {
    url?: string | null;
    label?: string | null;
    _target?: string | null;
  };

  // Each slot group's schema default points at a fixed photo_gallery index
  // (0..13). When the property has fewer photos those indices resolve to
  // empty and the slider renders black. Pull the live gallery here and
  // wrap each row's preferred indices with modulo so every row always has
  // something to show out of the box. property_featured_image is the last
  // resort when the gallery is entirely empty.
  const gallery = useGallery() as Array<{ image?: string | null; original_url?: string | null; alt_text?: string | null; title?: string | null }>;
  const featuredImage = usePropertyField('property_featured_image') as string | null;

  const pickSrc = useCallback(
    (preferredIndex: number): string => {
      const len = gallery.length;
      if (len > 0) {
        const safe = ((preferredIndex % len) + len) % len;
        const photo = gallery[safe];
        return photo?.image || photo?.original_url || gallery[0]?.image || gallery[0]?.original_url || featuredImage || '';
      }
      return featuredImage || '';
    },
    [gallery, featuredImage],
  );

  const pickAlt = useCallback(
    (preferredIndex: number, fallback: string): string => {
      const len = gallery.length;
      if (len > 0) {
        const safe = ((preferredIndex % len) + len) % len;
        return gallery[safe]?.alt_text || gallery[safe]?.title || fallback;
      }
      return fallback;
    },
    [gallery],
  );

  const fallbacks = useMemo(
    () => ({
      kitchen:        { b: pickSrc(0),  a: pickSrc(1),  bAlt: pickAlt(0, 'Kitchen — Phase 1'),           aAlt: pickAlt(1, 'Kitchen — Phase 2') },
      bathroom:       { b: pickSrc(2),  a: pickSrc(3),  bAlt: pickAlt(2, 'A1 Bathroom — Phase 1'),       aAlt: pickAlt(3, 'A1 Bathroom — Phase 2') },
      living:         { b: pickSrc(4),  a: pickSrc(5),  bAlt: pickAlt(4, 'A1 open-concept — Phase 1'),   aAlt: pickAlt(5, 'A1 open-concept — Phase 2') },
      kitchenDetail:  { b: pickSrc(6),  a: pickSrc(7),  bAlt: pickAlt(6, 'Kitchen finish — Phase 1'),    aAlt: pickAlt(7, 'Kitchen finish — Phase 2') },
      shower:         { b: pickSrc(8),  a: pickSrc(9),  bAlt: pickAlt(8, 'Shower — Phase 1'),            aAlt: pickAlt(9, 'Shower — Phase 2') },
      laundry:        { b: pickSrc(10), a: pickSrc(11), bAlt: pickAlt(10, 'Washer / dryer — Phase 1'),   aAlt: pickAlt(11, 'Washer / dryer — Phase 2') },
      lock:           { b: pickSrc(12), a: pickSrc(13), bAlt: pickAlt(12, 'Lock — Phase 1'),             aAlt: pickAlt(13, 'Smart lock — Phase 2') },
    }),
    [pickSrc, pickAlt],
  );

  useCtaSheenStyles();

  const ctaUrl = (cta.url || '').trim() || '/floor-plans';
  const ctaLabel = (cta.label || '').trim() || 'Select Your Floorplan';
  const ctaTarget = cta._target || '_self';

  // Deliberately NOT using useScrollReveal / useStaggerReveal here. In the
  // editor's sandboxed render the IntersectionObserver sometimes never fires
  // and the section stays at opacity: 0 — looking like a solid black
  // rectangle on the editor's dark backdrop. A plain, always-visible render
  // is more important than a scroll-in animation on this page.
  return (
    <section
      className="lux-finishes-section py-12 md:py-20"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Rendered in paired order (kitchen/kitchen-detail, bathroom/shower,
            living/laundry) so related finishes sit side-by-side on wider
            viewports. Lock takes a full-width row at the bottom. */}
        <div className="lux-ba-stack">
          <EditableSlotGroup
            groupPath="luxuryfinishes.compare_kitchen"
            definition={kitchenCompareDef}
            className="lux-ba-cell"
          >
            <BeforeAfterSlider
              data={kitchen}
              beforeFallbackSrc={fallbacks.kitchen.b}
              afterFallbackSrc={fallbacks.kitchen.a}
              beforeFallbackAlt={fallbacks.kitchen.bAlt}
              afterFallbackAlt={fallbacks.kitchen.aAlt}
            />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="luxuryfinishes.compare_kitchen_detail"
            definition={kitchenDetailCompareDef}
            className="lux-ba-cell"
          >
            <BeforeAfterSlider
              data={kitchenDetail}
              beforeFallbackSrc={fallbacks.kitchenDetail.b}
              afterFallbackSrc={fallbacks.kitchenDetail.a}
              beforeFallbackAlt={fallbacks.kitchenDetail.bAlt}
              afterFallbackAlt={fallbacks.kitchenDetail.aAlt}
            />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="luxuryfinishes.compare_bathroom"
            definition={bathroomCompareDef}
            className="lux-ba-cell"
          >
            <BeforeAfterSlider
              data={bathroom}
              beforeFallbackSrc={fallbacks.bathroom.b}
              afterFallbackSrc={fallbacks.bathroom.a}
              beforeFallbackAlt={fallbacks.bathroom.bAlt}
              afterFallbackAlt={fallbacks.bathroom.aAlt}
            />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="luxuryfinishes.compare_shower"
            definition={showerCompareDef}
            className="lux-ba-cell"
          >
            <BeforeAfterSlider
              data={shower}
              beforeFallbackSrc={fallbacks.shower.b}
              afterFallbackSrc={fallbacks.shower.a}
              beforeFallbackAlt={fallbacks.shower.bAlt}
              afterFallbackAlt={fallbacks.shower.aAlt}
            />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="luxuryfinishes.compare_living"
            definition={livingCompareDef}
            className="lux-ba-cell"
          >
            <BeforeAfterSlider
              data={living}
              beforeFallbackSrc={fallbacks.living.b}
              afterFallbackSrc={fallbacks.living.a}
              beforeFallbackAlt={fallbacks.living.bAlt}
              afterFallbackAlt={fallbacks.living.aAlt}
            />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="luxuryfinishes.compare_laundry"
            definition={laundryCompareDef}
            className="lux-ba-cell"
          >
            <BeforeAfterSlider
              data={laundry}
              beforeFallbackSrc={fallbacks.laundry.b}
              afterFallbackSrc={fallbacks.laundry.a}
              beforeFallbackAlt={fallbacks.laundry.bAlt}
              afterFallbackAlt={fallbacks.laundry.aAlt}
            />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="luxuryfinishes.compare_lock"
            definition={lockCompareDef}
            className="lux-ba-cell lux-ba-cell--wide"
          >
            <BeforeAfterSlider
              data={lock}
              beforeFallbackSrc={fallbacks.lock.b}
              afterFallbackSrc={fallbacks.lock.a}
              beforeFallbackAlt={fallbacks.lock.bAlt}
              afterFallbackAlt={fallbacks.lock.aAlt}
            />
          </EditableSlotGroup>
        </div>

        <div className="lux-finishes-cta-wrap">
          <EditableSlotGroup
            groupPath="luxuryfinishes.cta"
            definition={ctaDef}
            className="lux-finishes-cta-slot"
          >
            <a
              className="lux-finishes-cta vantage-cta-sheen"
              href={ctaUrl}
              target={ctaTarget}
              rel={ctaTarget === '_blank' ? 'noopener noreferrer' : undefined}
            >
              {ctaLabel}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Explicit section background — do NOT rely on
           bg-[var(--vibe-color-background)] because that CSS variable can be
           undefined in editor sandboxes, which lets a dark editor backdrop
           bleed through and makes the whole section look black. */
        .lux-finishes-section {
          background: var(--vibe-color-background, #ffffff);
          color: var(--vibe-color-text, #111111);
        }

        /* Single column through tablet and smaller desktops; 2-column grid
           on large desktops so related sliders (Kitchen pair, Bathroom pair,
           Living pair) sit side by side. */
        .lux-ba-stack {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 1280px) {
          .lux-ba-stack {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 1.25rem;
          }
        }

        .lux-ba-cell {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }
        /* Lock sits alone, centered on its own row. Explicit width because
           auto inline margins on a grid item shrink it to fit-content. */
        @media (min-width: 1280px) {
          .lux-ba-cell--wide {
            grid-column: 1 / -1;
            width: calc(50% - 0.625rem);
            margin-inline: auto;
          }
        }

        .lux-ba {
          margin: 0;
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          border-radius: 4px;
          overflow: hidden;
          background: #f1f1f3;
        }

        /* Positioning context for all the layered images, clip wrap, divider
           and range input. */
        .lux-ba-stage {
          position: relative;
          width: 100%;
          /* Padding-bottom hack is a reliable aspect-ratio fallback in case
             the modern aspect-ratio property does not apply in the editor
             sandbox. 66.666% is a 3:2 ratio (height / width). */
          padding-bottom: 66.666%;
          height: 0;
          overflow: hidden;
          background: #e8e8ea;
          user-select: none;
        }
        @supports (aspect-ratio: 3 / 2) {
          .lux-ba-stage {
            aspect-ratio: 3 / 2;
            padding-bottom: 0;
            height: auto;
          }
        }

        .lux-ba-img {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          /* Default: fill parent — correct for the base image. The overlay
             image overrides this to 100cqi so it fills the stage, not the
             (narrower) reveal wrap. */
          width: 100%;
          object-fit: cover;
          display: block;
          pointer-events: none;
        }
        .lux-ba-placeholder {
          background: repeating-linear-gradient(
            45deg,
            #e8e8ea 0,
            #e8e8ea 12px,
            #d8d8db 12px,
            #d8d8db 24px
          );
        }

        /* Overlay layer: covers the full stage, same size as the base image.
           clip-path on this wrap trims the right portion. Because width/height
           never change as the user drags, the image NEVER scales or shifts —
           only the clip edge moves. */
        .lux-ba-clip {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 1;
          will-change: clip-path;
        }
        .lux-ba-img--overlay {
          /* Fills the clip wrap, which fills the stage. Full-size — no cqi
             container-query dependency. */
          width: 100%;
          height: 100%;
        }

        /* Native range input stretched across the entire stage. Visually
           hidden (opacity: 0) but fully interactive — handles mouse, touch,
           keyboard, pen, and screen readers. z-index puts it above everything
           visual. */
        .lux-ba-range {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
          background: transparent;
          cursor: ew-resize;
          -webkit-appearance: none;
          appearance: none;
          opacity: 0;
          z-index: 3;
          touch-action: none;
        }
        .lux-ba-range:focus {
          outline: none;
        }
        .lux-ba-range::-webkit-slider-runnable-track {
          height: 100%;
          background: transparent;
          border: none;
        }
        .lux-ba-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 48px;
          height: 48px;
          background: transparent;
          border: none;
          cursor: ew-resize;
        }
        .lux-ba-range::-moz-range-track {
          background: transparent;
          border: none;
          height: 100%;
        }
        .lux-ba-range::-moz-range-thumb {
          width: 48px;
          height: 48px;
          background: transparent;
          border: none;
          cursor: ew-resize;
        }

        /* Visual divider line + knob. Positioned via the same --lux-ba-pos
           variable the overlay width uses so they always line up perfectly. */
        .lux-ba-divider {
          position: absolute;
          top: 0;
          bottom: 0;
          left: 50%;                 /* inline style overrides this per-slider */
          width: 2px;
          margin-left: -1px;
          background: #ffffff;
          box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
          pointer-events: none;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .lux-ba-knob {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: #ffffff;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2px;
          color: var(--vibe-color-primary);
          transition: box-shadow 0.15s ease;
        }
        /* Focus ring on the invisible range surfaces visually on the knob. */
        .lux-ba-range:focus-visible ~ .lux-ba-divider .lux-ba-knob {
          box-shadow: 0 0 0 3px var(--vibe-color-primary),
                      0 0 0 6px rgba(255, 255, 255, 0.7);
        }

        .lux-ba-arrow {
          display: inline-block;
          width: 0;
          height: 0;
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
        }
        .lux-ba-arrow--left {
          border-right: 8px solid currentColor;
        }
        .lux-ba-arrow--right {
          border-left: 8px solid currentColor;
        }

        .lux-ba-captions {
          flex: 1 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          background: var(--vibe-color-primary);
        }
        .lux-ba-caption {
          font-family: var(--vibe-font-heading);
          /* Small, tight, responsive. Allowed to wrap (text-wrap: balance
             keeps "Kitchen Finishes Phase 1" on two balanced lines instead
             of a short orphan). min-width: 0 lets grid cells shrink below
             their intrinsic text size. */
          font-size: 0.6875rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          line-height: 1.25;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-primary, #fff);
          background: var(--vibe-color-primary);
          padding: 0.6rem 0.5rem;
          /* Reserve two lines (2 × line-height 1.25) plus vertical padding
             so every card is the same height whether its caption wraps. */
          min-height: calc(2.5em + 1.2rem);
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          min-width: 0;
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: break-word;
          text-wrap: balance;
          hyphens: auto;
        }
        /* Thin vertical divider line between the two captions, matching the
           dark separator in the reference design. */
        .lux-ba-caption + .lux-ba-caption {
          border-left: 1px solid rgba(255, 255, 255, 0.35);
          box-shadow: inset 1px 0 0 rgba(0, 0, 0, 0.15);
        }
        @media (min-width: 480px) {
          .lux-ba-caption {
            font-size: 0.75rem;
            padding: 0.7rem 0.75rem;
            min-height: calc(2.5em + 1.4rem);
            letter-spacing: 0.07em;
          }
        }
        @media (min-width: 640px) {
          .lux-ba-caption {
            font-size: 0.8125rem;
            padding: 0.75rem 1rem;
            min-height: calc(2.5em + 1.5rem);
            letter-spacing: 0.08em;
          }
        }
        @media (min-width: 1024px) {
          .lux-ba-caption {
            font-size: 0.875rem;
            padding: 0.875rem 1.25rem;
            min-height: calc(2.5em + 1.75rem);
          }
        }

        .lux-finishes-cta-wrap {
          margin-top: 3rem;
          display: flex;
          justify-content: center;
        }
        .lux-finishes-cta-slot {
          display: inline-flex;
        }
        .lux-finishes-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.9rem 2.25rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.875rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          min-width: 260px;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'LuxuryFinishes',
  displayName: 'Luxury Finishes',
  tokens: {},
  slotGroups: {
    'luxuryfinishes.compare_kitchen': kitchenCompareDef,
    'luxuryfinishes.compare_bathroom': bathroomCompareDef,
    'luxuryfinishes.compare_living': livingCompareDef,
    'luxuryfinishes.compare_kitchen_detail': kitchenDetailCompareDef,
    'luxuryfinishes.compare_shower': showerCompareDef,
    'luxuryfinishes.compare_laundry': laundryCompareDef,
    'luxuryfinishes.compare_lock': lockCompareDef,
    'luxuryfinishes.cta': ctaDef,
  },
};

(LuxuryFinishes as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default LuxuryFinishes;
